<template>
  <main class="catalog-page">
    <section class="hero">
      <p class="eyebrow">Katalog stanova</p>
      <h1>Galerija po blokovima</h1>
      <!-- <p class="lead">
        Svi planovi stanova su ucitani direktno iz foldera u assets. Izaberi blok i pregledaj
        slike po podfolderima.
      </p> -->
    </section>

    <section class="master-render">
      <h2>Gde su blokovi zgrade</h2>
      <p>
        Prikaz spoljnog rendera kompleksa sa oznakama blokova i njihovim pozicijama.
      </p>
      <button type="button" class="master-render-image" @click="openPreview(masterRenderPreview)">
        <img :src="mainRender" alt="Glavni render MBH3 kompleksa sa oznacenim blokovima" loading="lazy" />
      </button>
    </section>

    <section class="controls">
      <label for="blockFilter">Blok</label>
      <select id="blockFilter" v-model="selectedBlock">
        <option value="ALL">Svi blokovi</option>
        <option v-for="block in availableBlocks" :key="block" :value="block">
          {{ block }}
        </option>
      </select>
      <div class="meta">Ukupno slika: {{ visibleImageCount }}</div>
    </section>

    <section v-if="groupedCatalog.length" class="catalog">
      <article v-for="group in groupedCatalog" :key="group.block" class="block-group">
        <header class="block-header">
          <h2>Blok {{ group.block }}</h2>
        </header>

        <section v-for="section in group.sections" :key="section.name" class="range-group">
          <h3>{{ section.name }}</h3>
          <div class="cards">
            <button
              v-for="item in section.items"
              :key="item.id"
              class="card"
              type="button"
              @click="openPreview(item)"
            >
              <img :src="item.src" :alt="item.apartmentLabel" loading="lazy" />
              <span class="caption">{{ item.apartmentLabel }}</span>
            </button>
          </div>
        </section>
      </article>
    </section>

    <section v-else class="empty-state">
      <h2>Nema slika za izabrani filter</h2>
      <p>Proveri da li su folderi i fajlovi pravilno postavljeni u src/assets.</p>
    </section>

    <aside v-if="activeImage" class="lightbox" @click.self="closePreview">
      <figure>
        <button type="button" class="close" @click="closePreview">Zatvori</button>
        <img :src="activeImage.src" :alt="activeImage.apartmentLabel" />
        <figcaption>
          <strong>{{ activeImage.apartmentLabel }}</strong>
          <span>Blok {{ activeImage.block }} / {{ activeImage.section }}</span>
        </figcaption>
      </figure>
    </aside>
  </main>
</template>

<script setup>
import { computed, ref } from 'vue'
import mainRender from '../assets/main_mbh3_2.jpg'

const selectedBlock = ref('ALL')
const activeImage = ref(null)

const masterRenderPreview = {
  id: 'main_mbh3_2.jpg',
  src: mainRender,
  apartmentLabel: 'Render celog MBH3 kompleksa',
  block: 'Svi',
  section: 'Pozicije blokova'
}

// require.context lets Vue CLI (webpack) include all images under assets at build time.
// eslint-disable-next-line no-undef
const imageContext = require.context('../assets', true, /\.(png|jpe?g|webp)$/i)

const images = imageContext
  .keys()
  .map((key) => {
    const cleanPath = key.replace('./', '')
    const parts = cleanPath.split('/')

    if (parts.length < 3) {
      return null
    }

    const [block, section, ...rest] = parts
    const fileName = rest.join('/')
    const apartmentLabel = fileName.replace(/\.[^/.]+$/, '')

    return {
      id: cleanPath,
      block,
      section,
      apartmentLabel,
      src: imageContext(key)
    }
  })
  .filter(Boolean)

const availableBlocks = computed(() => {
  return [...new Set(images.map((image) => image.block))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
  )
})

const groupedCatalog = computed(() => {
  const bucket = {}

  images.forEach((image) => {
    if (selectedBlock.value !== 'ALL' && image.block !== selectedBlock.value) {
      return
    }

    if (!bucket[image.block]) {
      bucket[image.block] = {}
    }

    if (!bucket[image.block][image.section]) {
      bucket[image.block][image.section] = []
    }

    bucket[image.block][image.section].push(image)
  })

  return Object.keys(bucket)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
    .map((block) => {
      const sections = Object.keys(bucket[block])
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
        .map((sectionName) => ({
          name: sectionName,
          items: bucket[block][sectionName].sort((a, b) =>
            a.apartmentLabel.localeCompare(b.apartmentLabel, undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          )
        }))

      return {
        block,
        sections
      }
    })
})

const visibleImageCount = computed(() => {
  return groupedCatalog.value.reduce((sum, group) => {
    return sum + group.sections.reduce((innerSum, section) => innerSum + section.items.length, 0)
  }, 0)
})

function openPreview(item) {
  activeImage.value = item
}

function closePreview() {
  activeImage.value = null
}
</script>

<style scoped>
.catalog-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 20px 48px;
}

.hero {
  background: linear-gradient(130deg, #11222d 0%, #1f3b4a 100%);
  color: #f8fbfd;
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 18px 38px rgba(9, 22, 30, 0.18);
}

.eyebrow {
  margin: 0;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #f4c9bb;
}

.hero h1 {
  margin: 10px 0 10px;
  font-size: clamp(1.7rem, 3.5vw, 2.5rem);
}

.lead {
  margin: 0;
  max-width: 72ch;
  line-height: 1.55;
  color: #d8e4eb;
}

.master-render {
  margin-top: 16px;
  background: #ffffff;
  border: 1px solid #d6dde3;
  border-radius: 16px;
  padding: 14px;
}

.master-render h2 {
  margin: 0;
  font-size: 1.15rem;
  color: #11222d;
}

.master-render p {
  margin: 8px 0 10px;
  color: #536471;
}

.master-render-image {
  border: 0;
  padding: 0;
  background: transparent;
  cursor: zoom-in;
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
}

.master-render-image img {
  width: 100%;
  height: auto;
  max-height: none;
  object-fit: contain;
  display: block;
}

.controls {
  margin-top: 18px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  background: #ffffff;
  border: 1px solid #d6dde3;
  border-radius: 14px;
  padding: 12px;
}

.controls label {
  font-weight: 700;
}

.controls select {
  border: 1px solid #c4d0d8;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 0.95rem;
}

.meta {
  margin-left: auto;
  font-size: 0.92rem;
  color: #536471;
}

.catalog {
  margin-top: 20px;
  display: grid;
  gap: 20px;
}

.block-group {
  background: #ffffff;
  border: 1px solid #d6dde3;
  border-radius: 18px;
  padding: 16px;
}

.block-header {
  border-bottom: 1px solid #ebf0f4;
  margin-bottom: 10px;
}

.block-header h2 {
  margin: 0 0 10px;
  color: #11222d;
}

.range-group h3 {
  margin: 14px 0 10px;
  color: #d65a31;
}

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 12px;
}

.card {
  background: #f8fafc;
  border: 1px solid #dae2e8;
  border-radius: 12px;
  padding: 8px;
  display: grid;
  gap: 8px;
  text-align: left;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(7, 25, 35, 0.12);
}

.card img {
  width: 100%;
  height: 160px;
  object-fit: cover;
  border-radius: 8px;
}

.caption {
  font-size: 0.9rem;
  font-weight: 600;
  color: #11222d;
}

.empty-state {
  margin-top: 20px;
  border: 1px dashed #b7c5ce;
  border-radius: 16px;
  padding: 24px;
  background: #ffffff;
}

.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(9, 17, 22, 0.82);
  display: grid;
  place-items: center;
  z-index: 50;
  padding: 16px;
}

.lightbox figure {
  margin: 0;
  max-width: min(1024px, 95vw);
  max-height: 95vh;
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
  display: grid;
}

.lightbox img {
  width: 100%;
  max-height: 78vh;
  object-fit: contain;
  background: #f5f8fb;
}

.lightbox figcaption {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  font-size: 0.9rem;
}

.close {
  justify-self: end;
  margin: 8px;
  background: #11222d;
  color: #ffffff;
  border: 0;
  border-radius: 10px;
  padding: 8px 10px;
  cursor: pointer;
}

@media (max-width: 760px) {
  .catalog-page {
    padding: 14px 10px 26px;
  }

  .hero {
    padding: 18px;
  }

  .meta {
    margin-left: 0;
    width: 100%;
  }
}
</style>
